import { useCallback, useEffect, useMemo, useState } from 'react';
import { KeyboardAvoidingView, Platform } from 'react-native';

import { bestE1rmFromPrRecords, fromCanonicalKg } from '@heraion/shared';

import { useLoggerContext } from './loggerContext';
import { WorkoutMetaFooter } from './WorkoutMetaFooter';
import { WorkoutNameEditor } from './WorkoutNameEditor';
import { useLoggerGymChip } from './LoggerGymChip';
import { useInWorkoutTargets } from './useInWorkoutTargets';
import { usePlanCues, cueForEntry } from './usePlanCues';
import { TimelineFocusSection } from './TimelineFocusSection';
import { TimelineHistorySheet } from './TimelineHistorySheet';
import {
  LoggerDockOutlet,
  LoggerDockProvider,
  LoggerDockSpacer,
} from './LoggerDock';
import { LoggerRestBar } from './LoggerRestBar';
import { LoggerRevealScroller } from './LoggerRevealScroller';
import { useWeightCandidates } from './useWeightCandidates';
import { LoggerCommitPrompts } from './LoggerCommitPrompts';
import { TimelineFeedSection } from './TimelineFeedSection';
import { rowPrefix, type FlashRow } from './timelineFlash';
import { useEffortCommit } from './useEffortCommit';
import { useTimelineLogger } from './useTimelineLogger';
import { useTimelineNotes } from './useTimelineNotes';
import { useWorkBoutAutoComplete } from './useWorkBoutAutoComplete';
import { isTodayDraftPending } from './state/loggerDraftHelpers';
import { useTodayDraft } from './useTodayDraft';

// Timeline-logger screen (Phase 2). The alternative logger body, rendered in
// place of the card list when Settings → Preferences → Logger is set to
// "Timeline". Reads the SAME logger context bag the card list reads, so it
// drives the SAME flat draft store — switching modes never forks the data
// (next/timeline-logger.md §5; the old card logger stays the live fallback).
//
// Three regions: a roster strip (switch the active exercise), the FocusPanel
// (the single data-entry point), and the reverse-chronological TimelineList
// feed. The focus state machine + the set/exercise handlers live in
// useTimelineLogger (Track R extraction); this file is the render shell only.
export function TimelineLoggerScreen() {
  const ctx = useLoggerContext();
  const {
    draft,
    energyReadout,
    dispatch,
    styles,
    theme,
    t,
    i18n,
    getExerciseNameBySlug,
    prHistory,
    liveRegion,
    submitting,
    totals,
    catalog,
    error,
    serverError,
    onSaveAsNew,
    defaultUnit,
    mode,
    setEffortScaleOpen,
    onCopyHistory,
    nameSuggestion,
    postSaveSummary,
    sessionResult,
    summaryCardRef,
    postSaveSharing,
    sharePostSave,
    hold,
  } = ctx;

  // Kept as one object as well as destructured: TimelineFocusSection takes the
  // whole bag, so the ~20 values its JSX reads cross the new file boundary as a
  // single typed prop instead of twenty hand-forwarded ones (CN-27).
  const tl = useTimelineLogger(ctx);
  const {
    activeEntry,
    activeEntryIdx,
    boundSet,
    boundSetIdx,
    activeExercise,
    effortScale,
    timelineRows,
    focusRow,
    uncompleteSet,
    onLogSet,
    onLogFail,
    onToggleFail,
    failPrompt,
    onConfirmFail,
    onCancelFail,
    amrapConfirm,
    closeHistory,
    rest,
    failFlash,
  } = tl;

  // HI-1 slice 2: a prescribed countdown bout ends itself at zero, through the
  // same onLogSet the athlete's tap uses. Mounted here because this is where
  // the timer and the completion leg already meet (see captureTimer below) —
  // the hook itself writes nothing.
  useWorkBoutAutoComplete({
    hold,
    entries: draft.entries,
    activeEntryIdx,
    boundSetIdx,
    onLogSet,
  });

  // LG-59 — the history widget's first section: every weight the app can work
  // out for the set in hand. Built here because this is where the bound set,
  // the maxes map and the athlete's scale are all already in hand; the sheet
  // stays a presentation of what it is handed.
  const historyCandidates = useWeightCandidates({
    entry: activeEntry,
    set: boundSet,
    currentMaxes: ctx.deps.maxesLoaded ? ctx.deps.maxMeta : null,
    effortScale,
    t,
  });

  // AP-3b — the in-workout contextual meter's rows for the active exercise,
  // with this live session's sets already folded into the weekly aggregate.
  const targetRows = useInWorkoutTargets(
    draft,
    activeExercise ?? null,
    catalog,
    effortScale,
    ctx.deps,
  );

  const [landedFocus, setLandedFocus] = useState<FlashRow>(null);
  // One-shot: the row's effect ran first, so clearing stops a later re-mount refocusing.
  useEffect(() => {
    if (landedFocus) setLandedFocus(null);
  }, [landedFocus]);

  // Effort-meter commit bag (AP-0b/0c): routes a meter tap onto the existing
  // commit legs (onLogSet with the tapped rir / onLogFail / onToggleFail) and
  // derives the meter's value/failed from the bound set. ALL set kinds — a
  // run or a plank carries effort like a squat («quando corro non mi sento
  // mai uguale»); schema and payload already accept rir on every kind.
  const effort = useEffortCommit({
    boundSet,
    // Measures, never prescribes: no onPrescribeEffort (useEffortCommit §PRESCRIBE).
    mode,
    activeEntryIdx,
    boundSetIdx,
    dispatch,
    onLogSet,
    onLogFail,
    onToggleFail,
    onOpenEffortScale: () => setEffortScaleOpen(true),
    // LG-78 — the dismissal and the signal are one fact, so they are written
    // together. Without this the signal can stay stuck on "typing" after a
    // dismissal RN did not turn into a blur, and the hand-over never fires.
    onKeyboardDismissed: (entryIdx, setIdx) => {
      ctx.fieldFocus.releaseAll();
      // #225: a touch browser lands focus on the completed row.
      setLandedFocus((f) => ({
        prefix: rowPrefix(entryIdx, setIdx),
        key: (f?.key ?? 0) + 1,
      }));
    },
    willPromptAmrap: amrapConfirm.shouldPrompt,
    // A band tap on a set whose timer is running also STOPS it and hands over
    // the seconds (MX-5 slice 3). Bound to the set being logged: capture()
    // returns null — and leaves the timer alone — when nothing is running or
    // when what's running belongs to another set, so the ordinary reps tap goes
    // through untouched and a plank running elsewhere is never harvested here.
    captureTimer: () => hold.capture(activeEntryIdx, boundSetIdx),
  });

  // Per-set / per-exercise coach cues re-resolved from the source program so
  // the 🎯 + 💬 reappear on EDIT / REPEAT (they're display-only, never saved —
  // Antenore 2026-06-19 regression). Same reference + offline cache as the
  // header "?" (useCoachNotes). The active entry's cues feed the FocusPanel as
  // a fallback; a live plan-start already carries them on the draft.
  const planCues = usePlanCues(draft.fromPlanId ?? draft.coachNotesRef, ctx.deps.apiFetch);
  // LG-34 — by (slug, rank), so three `walking` slots in one day each show
  // their own text instead of all three showing the first's. The memo guards
  // the re-renders that are NOT a dispatch: `draft.entries` is a fresh array
  // after every action, so on the logging path this recomputes regardless —
  // O(entries) and not worth more machinery than this.
  const activeCues = useMemo(
    () => cueForEntry(planCues, draft.entries, activeEntry),
    [planCues, draft.entries, activeEntry],
  );

  // Every exercise fully logged? Drives the FocusPanel-slot placeholder copy:
  // "all logged — add another or save" vs the start-of-workout "pick above".
  const allLogged =
    draft.entries.length > 0 &&
    draft.entries.every(
      (e) => e.sets.length > 0 && e.sets.every((s) => s.completedAt !== null),
    );

  // Timeline notes (Solution C): merge set rows + note events into one
  // chronological feed + own the composer handlers. The composer is the single
  // note-authoring surface (the per-entry 📝 menu is retired).
  // ⚠️ The note's context is WHICH EXERCISE, so it takes the catalog slug —
  // NOT the logger cursor, which since LG-39 carries the row's entryId. The
  // two used to be the same string and this argument silently meant both.
  const { feed, onSendNote, onDeleteNote } = useTimelineNotes(
    ctx,
    timelineRows,
    activeEntry?.exerciseId ?? null,
  );

  // Workout title: shown as compact text (the user's name, or the live
  // auto-suggestion when blank) with a ✎ to switch to an inline input.
  const [editingName, setEditingName] = useState(false);
  // EQ-1 slice D — the place rides that same row; the hook hands back the chip
  // and the tray separately so each can be placed where it belongs.
  const gymChip = useLoggerGymChip({
    value: draft.location ?? null,
    dispatch,
    // EQ-1-GYM-DEFAULT — the ★ that elects a default place, through the existing
    // profile door the rest-default picker already uses (hostDeps.patchProfile).
    defaultLocation: ctx.deps.profile.defaultLocation,
    patchProfile: ctx.deps.patchProfile,
    notify: ctx.deps.notify,
  });
  // Leave edit mode the moment a save starts so the title never gets stuck
  // showing a disabled input during the submit window.
  useEffect(() => {
    if (submitting) setEditingName(false);
  }, [submitting]);

  // `MX-10` — the number the card calls «best» is the athlete's ALL-TIME best,
  // and it comes from the server-computed PR records (`/api/me/records`) the
  // Logger already fetches and caches for the PR pill. ⛔ It is deliberately
  // NOT the logger's rolling history window: that window is 50 SESSIONS, and
  // Health Connect walk imports eat its slots, so a superlative measured on it
  // is a confident lie the moment an older session drops off the edge (his
  // barbell-row, 2026-09-07: «best» at e1RM 76 with 84 and 77 nine sessions
  // past the border).
  //
  // ⭐ And it is computed ONCE per records change, not per row per render.
  // The old reader walked all 50 cached sessions inside `renderE1rm`, for
  // every row, on every keystroke, memoised nowhere — the cost Antenore named
  // on 2026-09-07 ([[feedback_a_count_on_the_render_path_is_paid_every_keystroke]]).
  // What the render path does now is a map lookup.
  const bestE1rmBySlug = useMemo(() => {
    const out = new Map<string, number | null>();
    if (prHistory === null) return out;
    for (const slug of Object.keys(prHistory)) {
      out.set(slug, bestE1rmFromPrRecords(prHistory[slug]));
    }
    return out;
  }, [prHistory]);
  const bestE1rmForSlug = useCallback(
    // ⛔ `null` while the records have not hydrated: the card then shows the
    // e1RM with NO comparison, which is the state it already had on a cold
    // window. Falling back to the rolling window here would put the wrong
    // superlative back for exactly as long as the fetch takes.
    (slug: string) => bestE1rmBySlug.get(slug) ?? null,
    [bestE1rmBySlug],
  );

  // AP-9b: an unstarted «today» draft holds the focus slot, whatever is
  // focused (AP-9c). Start restarts the clock and leaves nothing focused: the
  // session begins when the athlete says so.
  const todayDraft = useTodayDraft({
    draft,
    dispatch,
    catalog,
    defaultUnit,
    deps: ctx.deps,
    resolveName: getExerciseNameBySlug,
    onStart: () => {
      tl.closeCard();
      dispatch({ type: 'start_today_draft', startedAt: new Date().toISOString() });
    },
    onDiscard: ctx.onCancel,
    ...(ctx.onOpenPainMap ? { onOpenPainMap: ctx.onOpenPainMap } : {}),
    ...(ctx.onOpenCreateHub ? { onOpenCreateHub: ctx.onOpenCreateHub } : {}),
    ...(ctx.onBuildAnyway ? { onBuildAnyway: ctx.onBuildAnyway } : {}),
    ...(ctx.onTodayRestHours ? { onRestHours: ctx.onTodayRestHours } : {}),
    ...(ctx.onTodayEmptyChange ? { onEmptyChange: ctx.onTodayEmptyChange } : {}),
    ...(ctx.weeklyMinimumMet !== undefined ? { weeklyMinimumMet: ctx.weeklyMinimumMet } : {}),
    theme,
  });

  // TM-18c — the docked rest bar reports its own height and the page reserves
  // exactly that much at the end of the scroll, so the last card (date +
  // duration) stays readable when the bar is up. 0 while no rest runs, which
  // collapses the spacer with the bar.
  return (
    <KeyboardAvoidingView
      style={styles.flex}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      {/* Context only, no View: the siblings below keep the exact layout they
          had, and the dock's own band is mounted by <LoggerDockOutlet/>. */}
      <LoggerDockProvider>
      {/* LG-98 — the same ScrollView, plus the one thing it could not do:
          bring the row being typed into back above the absolute dock. It owns
          the scroller so it can read the dock's height from inside the
          provider; the siblings around it are unchanged. */}
      <LoggerRevealScroller
        contentContainerStyle={styles.body}
        keyboardShouldPersistTaps="handled"
      >
        {/* EQ-1 slice D — WHERE this session is happening, on the session's
            own header row beside its name (Antenore, 2026-08-13). It sat in
            the roster header first and read as "completamente sconnesso":
            wedged between the EXERCISES label and the list's own chevron it
            looked like a control of the exercise list, which it is not. The
            place is a fact of the SESSION, so it belongs where the session
            names itself. The tray opens full-width underneath. */}
        <WorkoutNameEditor
          name={draft.name}
          nameSuggestion={nameSuggestion}
          editing={editingName}
          onStartEdit={() => setEditingName(true)}
          onStopEdit={() => setEditingName(false)}
          onChangeName={(text) => dispatch({ type: 'set_name', name: text })}
          submitting={submitting}
          trailing={gymChip.trigger}
          styles={styles}
          theme={theme}
          t={t}
        />
        {gymChip.tray}

        {/* Roster strip + FocusPanel — split to TimelineFocusSection when the
            shell sat at exactly 500 lines with no margin (CN-27), the same
            mould TimelineFeedSection set for the bottom of the screen. */}
        <TimelineFocusSection
          ctx={ctx}
          tl={tl}
          targetRows={targetRows}
          effort={effort}
          activeCues={activeCues}
          allLogged={allLogged}
          pendingSlot={todayDraft}
          pin={mode === 'log' ? ctx.pin : undefined}
        />

        {/* Note composer + add-exercise + reverse-chronological feed — split
            to TimelineFeedSection when this shell hit the 500-line cap
            (CN-27). Live during a rest, like everything else (TM-18b). */}
        <TimelineFeedSection
          ctx={ctx}
          feed={feed}
          onSendNote={onSendNote}
          onDeleteNote={onDeleteNote}
          onFocusRow={focusRow}
          onUncompleteSet={uncompleteSet}
          bestE1rmForSlug={bestE1rmForSlug}
          failFlash={failFlash}
          landedFocus={landedFocus}
        />

        <WorkoutMetaFooter
          postSaveSummary={postSaveSummary}
          sessionResultValue={sessionResult.result}
          energyReadout={energyReadout}
          totals={totals}
          draft={draft}
          todayDraft={isTodayDraftPending(draft)}
          error={error}
          serverError={serverError}
          onSaveAsNew={onSaveAsNew}
          submitting={submitting}
          postSaveSharing={postSaveSharing}
          sharePostSave={sharePostSave}
          dispatch={dispatch}
          defaultUnit={defaultUnit}
          locale={i18n.language}
          summaryCardRef={summaryCardRef}
          styles={styles}
          theme={theme}
          t={t}
          PostSessionSummaryCard={ctx.deps.PostSessionSummaryCard}
          profile={ctx.deps.profile}
        />

        {/* The room the dock occupies, measured from the dock itself. */}
        <LoggerDockSpacer />
      </LoggerRevealScroller>

      {/* ⏱ the rest hands its band to the dock — outside the ScrollView so it
          does not scroll away, and outside the card so it freezes nothing.
          It renders nothing here itself (LG-94). */}
      <LoggerRestBar rest={rest} theme={theme} />

      {/* ⛔ The dock goes HERE, before the sheet and the prompts: later
          siblings paint on top, and a rest timer drawn over a dialog is the
          bug this position prevents. */}
      <LoggerDockOutlet />

      {/* 🕐 history bottom sheet (extracted to TimelineHistorySheet, slice C). */}
      <TimelineHistorySheet
        entry={activeEntry}
        onClose={closeHistory}
        candidates={historyCandidates}
        max={
          activeEntry ? ctx.deps.maxMeta.get(activeEntry.exerciseId) : undefined
        }
        {...(ctx.deps.onEditMax && activeEntry
          ? {
              onEditMax: () =>
                ctx.deps.onEditMax?.(activeEntry.exerciseId),
            }
          : {})}
        onCopy={(sourceEntry) => {
          onCopyHistory(activeEntryIdx, sourceEntry);
          closeHistory();
        }}
        onCopySet={(patch) => {
          if (!boundSet) return;
          dispatch({
            type: 'update_set',
            entryIdx: activeEntryIdx,
            setIdx: boundSetIdx,
            patch: {
              ...(patch.weightKg === null
                ? {}
                : { weight: String(fromCanonicalKg(patch.weightKg, boundSet.unit)) }),
              // ⛔ reps only when the row HAS them: a hold or a cardio row
              // says nothing about a rep count, and writing '' would wipe the
              // one the program prescribed.
              ...(patch.reps === null || patch.reps === undefined
                ? {}
                : { reps: String(patch.reps) }),
            },
          });
          closeHistory();
        }}
        locale={i18n.language}
        unit={defaultUnit}
        effortScale={effortScale}
        getExerciseNameBySlug={getExerciseNameBySlug}
        t={t}
        theme={theme}
      />

      {/* The prompts raised BY a completion — fail rep-count (AP-0p) and the
          AMRAP question (LG-26). One host so they cannot stack. */}
      <LoggerCommitPrompts
        failPrompt={failPrompt}
        onConfirmFail={onConfirmFail}
        onCancelFail={onCancelFail}
        amrapPrompt={amrapConfirm.prompt}
        onConfirmAmrap={amrapConfirm.confirm}
        onDismissAmrap={amrapConfirm.dismiss}
      />
      </LoggerDockProvider>
      {liveRegion}
    </KeyboardAvoidingView>
  );
}
